import { memo } from 'react';
import { defineMessages, FormattedMessage, type MessageValue } from 'react-intl';

import { MenuHeader } from '@order/components-menu';

import {
  type MenuContentDeliveryDetailsData,
  type MenuContentOrderChannel,
  type MenuContentRestaurantData,
} from '../../../../MenuContent.types';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentHeaderDetails = memo((props: Props) => {
  const {
    locationName,
    orderChannel,
    deliveryDetailsData,
    hours,
    utcOffset,
    dataSet,
    shouldRenderMoreInfoButton,
    onChangeLocationPress,
    onMoreInfoPress,
  } = props;

  const isDelivery = orderChannel === 'delivery';

  // ─────────────────────────────────────────────────────────────────────

  return (
    <MenuHeader.ContentContainer dataSet={dataSet}>
      <MenuHeader.DetailsText>
        <MenuHeader.DetailsTextItem>
          <MenuHeader.DetailsLocationText locationName={locationName} orderChannel={orderChannel} />
        </MenuHeader.DetailsTextItem>

        {isDelivery && deliveryDetailsData?.completeAddress ? (
          <MenuHeader.DetailsTextItem withSeparator>
            {deliveryDetailsData.completeAddress}
          </MenuHeader.DetailsTextItem>
        ) : null}

        {isDelivery && deliveryDetailsData?.deliveryEstimate ? (
          <MenuHeader.DetailsTextItem withSeparator>
            <FormattedMessage
              {...messages.deliveryEtaText}
              values={{ eta: deliveryDetailsData.deliveryEstimate }}
            />
          </MenuHeader.DetailsTextItem>
        ) : null}

        {isDelivery ? null : (
          <MenuHeader.DetailsStoreHoursText
            storeHours={hours.store}
            permanentHours={hours.permanent}
            utcOffsetHours={utcOffset ?? undefined}
            orderChannel={orderChannel}
          />
        )}
      </MenuHeader.DetailsText>

      <MenuHeader.ChangeLocationButton onPress={onChangeLocationPress} />

      {shouldRenderMoreInfoButton ? <MenuHeader.MoreInfoButton onPress={onMoreInfoPress} /> : null}
    </MenuHeader.ContentContainer>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{ readonly deliveryEtaText: { readonly eta: MessageValue } }>({
  deliveryEtaText: {
    defaultMessage: 'Delivers in {eta} minutes',
    description: 'Menu Header | Delivery ETA text',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  locationName: string;
  orderChannel: MenuContentOrderChannel;
  deliveryDetailsData: MenuContentDeliveryDetailsData | undefined | null;
  hours: MenuContentRestaurantData['hours'];
  utcOffset: MenuContentRestaurantData['utcOffset'];
  dataSet?: Record<string, string>;
  shouldRenderMoreInfoButton: boolean;
  onChangeLocationPress: () => void;
  onMoreInfoPress: () => void;
};
